/**
 * Content Cache — Separate IndexedDB store for article content
 *
 * Decouples heavy content data (avg 46KB, up to 2MB) from Legend-State's
 * reactive observable tree. This prevents:
 * 1. Structured clone overhead on main thread during IDB writes
 * 2. Reactive chain triggering across all observers on content writes
 * 3. GC pressure from 30MB+ content in a single observable tree
 *
 * Uses a separate database (ReedleContentDB) to avoid version conflicts
 * with Legend-State's managed ReedleDB.
 *
 * Interface is storage-backend-agnostic: can swap IDB for OPFS later.
 *
 * @see docs/reedle/dev/indexdb-crash-fixing-plan.md
 */

const DB_NAME = 'ReedleContentDB';
const DB_VERSION = 1;
const STORE_NAME = 'articles';
const INIT_TIMEOUT_MS = 5000;

/** @type {IDBDatabase|null} */
let db = null;
let dbPromise = null;
let initFailed = false;

/**
 * Reset connection state so the next openDB() call retries from scratch.
 * Called on every failure path to prevent permanent "stuck null" state.
 */
function resetDBState() {
  db = null;
  dbPromise = null;
  initFailed = true;
}

/**
 * Open the content cache database.
 * Lazy-initialized on first access. Returns cached connection on subsequent calls.
 * On failure, resets internal state so the next call retries (no permanent failure).
 * @returns {Promise<IDBDatabase|null>}
 */
function openDB() {
  if (db) return Promise.resolve(db);
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve) => {
    let settled = false; // Guard against double-resolve (timeout vs onsuccess race)

    try {
      const req = indexedDB.open(DB_NAME, DB_VERSION);

      const timeout = setTimeout(() => {
        if (settled) return;
        settled = true;
        console.warn('[ContentCache] DB open timed out');
        resetDBState();
        resolve(null);
      }, INIT_TIMEOUT_MS);

      req.onupgradeneeded = (event) => {
        const database = event.target.result;
        if (!database.objectStoreNames.contains(STORE_NAME)) {
          database.createObjectStore(STORE_NAME);
        }
      };

      req.onsuccess = () => {
        clearTimeout(timeout);
        if (settled) {
          // Timeout already fired — close the late connection to avoid leaked handles
          try { req.result.close(); } catch { /* best effort */ }
          return;
        }
        settled = true;
        db = req.result;
        initFailed = false;

        // Handle unexpected close (e.g., browser clearing storage)
        db.onclose = () => {
          db = null;
          dbPromise = null;
        };

        // Handle version change from another tab (e.g., app update)
        db.onversionchange = () => {
          db.close();
          db = null;
          dbPromise = null;
        };

        resolve(db);
      };

      req.onerror = () => {
        clearTimeout(timeout);
        if (settled) return;
        settled = true;
        console.error('[ContentCache] DB open error:', req.error);
        resetDBState();
        resolve(null);
      };

      req.onblocked = () => {
        clearTimeout(timeout);
        if (settled) return;
        settled = true;
        console.warn('[ContentCache] DB open blocked');
        resetDBState();
        resolve(null);
      };
    } catch (e) {
      console.error('[ContentCache] DB open exception:', e);
      resetDBState();
      resolve(null);
    }
  });

  return dbPromise;
}

/**
 * Get cached content for an article.
 * @param {string} id - Article ID
 * @returns {Promise<{content: string, extra_metadata: object, hidden_block_indices: number[]}|null>}
 */
export async function getContent(id) {
  const database = await openDB();
  if (!database) return null;

  return new Promise((resolve) => {
    try {
      const tx = database.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const req = store.get(id);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => {
        console.warn('[ContentCache] get error:', req.error);
        resolve(null);
      };
    } catch (e) {
      console.warn('[ContentCache] get exception:', e);
      resolve(null);
    }
  });
}

/**
 * Store content for an article.
 * @param {string} id - Article ID
 * @param {{content: string, extra_metadata?: object, hidden_block_indices?: number[]}} data
 * @returns {Promise<boolean>} true if write succeeded, false on failure
 */
export async function setContent(id, data) {
  const database = await openDB();
  if (!database) return false;

  return new Promise((resolve) => {
    try {
      const tx = database.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      store.put(data, id);
      tx.oncomplete = () => resolve(true);
      tx.onabort = () => {
        console.warn('[ContentCache] set aborted:', tx.error);
        resolve(false);
      };
      tx.onerror = () => {
        console.warn('[ContentCache] set error:', tx.error);
        resolve(false);
      };
    } catch (e) {
      console.warn('[ContentCache] set exception:', e);
      resolve(false);
    }
  });
}

/**
 * Store content for multiple articles in a single transaction.
 * Critical for background fetcher performance — 1 transaction per batch.
 * @param {Array<{id: string, data: {content: string, extra_metadata?: object, hidden_block_indices?: number[]}}>} entries
 * @returns {Promise<boolean>} true if write succeeded, false on failure
 */
export async function bulkSetContent(entries) {
  if (!entries || entries.length === 0) return true;

  const database = await openDB();
  if (!database) return false;

  return new Promise((resolve) => {
    try {
      const tx = database.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      for (const { id, data } of entries) {
        store.put(data, id);
      }
      tx.oncomplete = () => resolve(true);
      tx.onabort = () => {
        console.warn('[ContentCache] bulkSet aborted:', tx.error);
        resolve(false);
      };
      tx.onerror = () => {
        console.warn('[ContentCache] bulkSet error:', tx.error);
        resolve(false);
      };
    } catch (e) {
      console.warn('[ContentCache] bulkSet exception:', e);
      resolve(false);
    }
  });
}

/**
 * Get content for multiple articles in a single IDB transaction.
 * Returns a Map of id → content string. Articles without cached content are omitted.
 * @param {string[]} ids - Article IDs
 * @returns {Promise<Map<string, string>>}
 */
export async function bulkGetContentStrings(ids) {
  const result = new Map();
  if (!ids || ids.length === 0) return result;

  const database = await openDB();
  if (!database) return result;

  return new Promise((resolve) => {
    try {
      const tx = database.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      let pending = ids.length;
      let resolved = false;

      const finish = () => {
        if (resolved) return;
        resolved = true;
        resolve(result);
      };

      // If transaction aborts, resolve with partial results instead of hanging
      tx.onabort = () => finish();

      for (const id of ids) {
        const req = store.get(id);
        req.onsuccess = () => {
          if (req.result?.content) {
            result.set(id, req.result.content);
          }
          if (--pending === 0) finish();
        };
        req.onerror = () => {
          if (--pending === 0) finish();
        };
      }
    } catch (e) {
      console.warn('[ContentCache] bulkGetContentStrings exception:', e);
      resolve(result);
    }
  });
}

/**
 * Delete cached content for an article.
 * @param {string} id - Article ID
 * @returns {Promise<void>}
 */
export async function deleteContent(id) {
  const database = await openDB();
  if (!database) return;

  return new Promise((resolve) => {
    try {
      const tx = database.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      store.delete(id);
      tx.oncomplete = () => resolve();
      tx.onerror = () => {
        console.warn('[ContentCache] delete error:', tx.error);
        resolve();
      };
    } catch (e) {
      console.warn('[ContentCache] delete exception:', e);
      resolve();
    }
  });
}

/**
 * Check if content is cached for an article.
 * Uses count() which is faster than get() for existence checks.
 * @param {string} id - Article ID
 * @returns {Promise<boolean>}
 */
export async function hasContent(id) {
  const database = await openDB();
  if (!database) return false;

  return new Promise((resolve) => {
    try {
      const tx = database.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const req = store.count(id);
      req.onsuccess = () => resolve(req.result > 0);
      req.onerror = () => resolve(false);
    } catch {
      resolve(false);
    }
  });
}

/**
 * Get all cached article IDs. Uses key cursor (no value loading) for speed.
 * @returns {Promise<Set<string>>}
 */
export async function getAllCachedIds() {
  const database = await openDB();
  if (!database) return new Set();

  return new Promise((resolve) => {
    try {
      const tx = database.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const req = store.getAllKeys();
      req.onsuccess = () => resolve(new Set(req.result));
      req.onerror = () => {
        console.warn('[ContentCache] getAllCachedIds error:', req.error);
        resolve(new Set());
      };
    } catch (e) {
      console.warn('[ContentCache] getAllCachedIds exception:', e);
      resolve(new Set());
    }
  });
}

/**
 * Clear all cached content and reset in-memory ID cache.
 * Used during logout, force update, etc.
 * Callers do NOT need to separately call clearCachedIdsSync() — this handles it.
 * @returns {Promise<void>}
 */
export async function clearContentCache() {
  // Always reset the in-memory ID cache, regardless of DB state
  clearCachedIdsSync();

  const database = await openDB();
  if (!database) {
    // If DB isn't open, try to delete the whole database
    try {
      indexedDB.deleteDatabase(DB_NAME);
    } catch {
      // Best effort
    }
    return;
  }

  return new Promise((resolve) => {
    try {
      const tx = database.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      store.clear();
      tx.oncomplete = () => resolve();
      tx.onerror = () => {
        console.warn('[ContentCache] clear error:', tx.error);
        resolve();
      };
    } catch (e) {
      console.warn('[ContentCache] clear exception:', e);
      resolve();
    }
  });
}

/**
 * Get diagnostic info for debugging.
 * @returns {Promise<{isOpen: boolean, initFailed: boolean, entryCount: number}>}
 */
export async function getStatus() {
  const database = await openDB();
  let entryCount = 0;

  if (database) {
    try {
      entryCount = await new Promise((resolve) => {
        const tx = database.transaction(STORE_NAME, 'readonly');
        const req = tx.objectStore(STORE_NAME).count();
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => resolve(0);
      });
    } catch {
      // Best effort
    }
  }

  return {
    dbName: DB_NAME,
    isOpen: !!db,
    initFailed,
    entryCount,
  };
}

// In-memory cache of known IDs for sync access (populated on init)
let _cachedIds = null;
let _cachedIdsPromise = null;

/**
 * Initialize the in-memory ID set. Call once on app startup.
 * After this, hasCachedContentSync() can be used for synchronous checks.
 * @returns {Promise<void>}
 */
export async function initCachedIds() {
  if (_cachedIdsPromise) return _cachedIdsPromise;
  _cachedIdsPromise = getAllCachedIds().then(ids => {
    _cachedIds = ids;
  });
  return _cachedIdsPromise;
}

/**
 * Synchronous check if content is cached (uses in-memory Set).
 * Returns false if initCachedIds() hasn't completed yet.
 * @param {string} id - Article ID
 * @returns {boolean}
 */
export function hasCachedContentSync(id) {
  return _cachedIds ? _cachedIds.has(id) : false;
}

/**
 * Mark an ID as cached in the in-memory Set.
 * Called after setContent/bulkSetContent to keep sync cache in sync.
 * @param {string} id - Article ID
 */
export function markCachedSync(id) {
  if (_cachedIds) _cachedIds.add(id);
}

/**
 * Remove an ID from the in-memory Set.
 * Called after deleteContent to keep sync cache in sync.
 * @param {string} id - Article ID
 */
export function unmarkCachedSync(id) {
  if (_cachedIds) _cachedIds.delete(id);
}

/**
 * Clear the in-memory ID set and reset the init promise.
 * This ensures a subsequent initCachedIds() call re-reads from IDB
 * instead of returning the stale (now-empty) Set.
 */
export function clearCachedIdsSync() {
  _cachedIds = new Set();
  _cachedIdsPromise = null;
}

// Export DB_NAME for cleanup utilities
export const CONTENT_DB_NAME = DB_NAME;
